<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import type { Actor, Dentist, HealthCenter, Reference } from "@/types";
import { pictogram } from "@/types";
import { ref, onMounted, onUpdated, computed, inject } from "vue";
import { actorsKey, dentistKey, healthCentersKey } from "@/logic/provide";
import { findStakeholder } from "@/logic/search";

const props = withDefaults(
  defineProps<{
    label: String;
    warning: string;
    model: Array<Reference>;
  }>(),
  {
    warning: "",
  }
);

const allActors = inject(actorsKey);
const allPrct = inject(dentistKey);
const allHealthCenters = inject(healthCentersKey);

const labelRef = ref<HTMLLabelElement | null>(null);
const notchLeadingWidth = ref(9);
const notchMiddleWidth = ref(0);

const notchRef = ref<HTMLElement | null>(null);

const listModel = computed(() => {
  if (!allActors || !allPrct || !allHealthCenters) {
    console.error("Missing at least one search source");
    return [];
  }
  const hydrated = findStakeholder(
    props.model,
    allActors,
    allPrct,
    allHealthCenters
  );
  const all: Array<Actor | Dentist | HealthCenter | string> =
    hydrated?.found || [];
  hydrated?.notFound.forEach((r) => {
    all?.push(r.name);
  });
  return all;
});

function recomputeNotchWidth() {
  if (labelRef.value) {
    notchMiddleWidth.value = labelRef.value.clientWidth + 5;
  }
}

onMounted(() => {
  recomputeNotchWidth();
});

onUpdated(() => {
  recomputeNotchWidth();
});
</script>

<template>
  <div class="input-input" ref="inputRef">
    <template v-for="model in listModel">
      <template v-if="typeof model == 'string'"> {{ model }} </template>
      <template v-else>
        <!-- person -->
        <div
          class="content"
          v-if="pictogram(model) == 'human' && 'first_name' in model"
          :key="model.id"
        >
          <img src="@/assets/icons/actor-small.svg" class="minicon" />
          <span>{{ model.first_name }} </span>
          <b>{{ model.last_name }}</b>
        </div>

        <!-- student -->
        <div
          class="content"
          v-if="pictogram(model) == 'student' && 'first_name' in model"
          :key="model.id"
        >
          <img src="@/assets/icons/student-small.svg" class="minicon" />
          <span>{{ model.first_name }} </span>
          <b>{{ model.last_name }}</b>
        </div>

        <!-- health center  -->
        <div
          class="content"
          v-if="pictogram(model) == 'institute' && 'name' in model"
          :key="model.id"
        >
          <img src="@/assets/icons/medical-center-small.svg" class="minicon" />
          <b>
            {{ ("other_name" in model && model.other_name) || model.name }}
          </b>
          <template v-if="model.contact_imported.zipcode">
            <span> - </span>
            <span> {{ model.contact_imported.zipcode }} </span>
          </template>
        </div>

        <!-- institution -->
        <div
          class="content"
          v-if="pictogram(model) == 'institute' && 'institution_name' in model"
          :key="model.id"
        >
          <img src="@/assets/icons/building-small.svg" class="minicon" />
          <b>
            {{ model.institution_name || model.last_name }}
          </b>
          <span> ({{ "type" in model && model.type }})</span>
          <template v-if="model.contact?.zipcode">
            <span> - </span>
            <span> {{ model.contact.zipcode }} </span>
          </template>
        </div>

        <!-- practitioner -->
        <div
          class="content"
          v-if="pictogram(model) == 'practitian' && 'first_name' in model"
          :key="model.id"
        >
          <img src="@/assets/icons/id-card-small.svg" class="minicon" />
          <span>{{ model.first_name }} </span>
          <b>{{ model.last_name }}</b>
        </div>
      </template>
    </template>
  </div>
  <label
    v-if="label"
    ref="labelRef"
    class="input-label"
    :class="{ warn: warning !== '' }"
  >
    {{ label }}
  </label>
  <span class="warning-text" v-if="warning !== ''">{{ warning }}</span>
  <div class="input-notch" ref="notchRef" :disabled="true">
    <div
      class="input-notch-leading"
      :style="{ width: `${notchLeadingWidth}px` }"
    ></div>
    <div
      class="input-notch-middle"
      :style="{ width: `${notchMiddleWidth}px` }"
    ></div>
    <div class="input-notch-trailing"></div>
  </div>
</template>

<style lang="scss">
// Removing the arrows for numbers

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}

input.input-input,
.input-input {
  display: block;
  width: 100%;

  border: 0;
  border-radius: 3px;

  padding-top: 0.32rem;
  padding-bottom: 0.32rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;

  margin-top: 0.5em;

  background: var(--color-background-soft-contrast);
  transition: all 0.2s linear;

  border-color: #bdbdbd;

  &:focus {
    ~ {
      .input-notch {
        div {
          border-color: var(--vt-c-purple);
          border-width: 2px;
        }
      }
    }
  }
}

// Inherited from form input
.input-label {
  position: absolute;
  top: -0.52em; // -1 +margin-top -0.2
  left: 0.75rem;

  padding-top: 0.37rem;
  margin-bottom: 0;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;

  transition: all 0.2s ease-out;

  color: var(--vt-c-purple);

  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;

  &.warn {
    text-decoration: underline;
    text-decoration-color: var(--color-background-warning);
  }
}

.input-footer {
  position: absolute;
  bottom: -0.6em;
  left: 0.7em;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;

  transition: all 0.2s ease-out;

  color: rgba(33, 53, 71, 0.6);

  font-style: normal;
  font-weight: 500;
  font-size: 8px;
  line-height: 10px;
}

.input-notch {
  display: flex;
  position: absolute;
  left: 0;
  bottom: 0;

  width: 100%;
  max-width: 100%;
  height: calc(100% - 0.5em);

  text-align: left;
  pointer-events: none;

  div {
    pointer-events: none;
    border: 1px solid;
    background: rgba(0, 0, 0, 0);
  }

  &[disabled="true"] div {
    border: none;
  }

  .input-notch-leading {
    border-right: none;
    border-radius: 0.25rem 0 0 0.25rem;
  }

  .input-notch-middle {
    border-right: none;
    border-left: none;
    border-top: none;
  }

  .input-notch-trailing {
    flex-grow: 1; // fill space
    border-left: none;
    border-radius: 0 0.25rem 0.25rem 0;
  }
}

.warning-text {
  visibility: hidden;
  position: absolute;
  z-index: 100;

  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
}

.input-input:hover ~ .warning-text {
  visibility: visible;
  animation: visibility ease-in-out 0.2s;
}

// Listing
.content {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  column-gap: 0.4em;
  margin-top: 0.2em;

  align-items: baseline;
}

.minicon {
  height: 14px;
  width: 14px;

  svg {
    fill: var(--color-background-soft-contrast);
  }
}
</style>
